import { ApplicationUser } from 'api/interfaces/applicationUser';
import { PrismApplication } from 'api/interfaces/prismApplication';
import { Form, FormChangeResult } from 'components/Form';
import config from 'config';
import { onIpAddressesAdded } from 'helpers/ipAddressesListParser';
import React from 'react';
import { connect, MapDispatchToProps, MapStateToProps } from 'react-redux';
import { reloadUser } from 'redux/actions/usersForm/reloadUser';
import { ApplicationState } from 'redux/applicationState';
import { UsersFormState } from 'redux/reducers/usersFormReducer';
import fields from 'routes/EntityManagement/UsersManagement/components/UsersForm/fields';
import styles from 'routes/EntityManagement/UsersManagement/users-management.module.scss';
import { DropdownOption } from 'types/dropdownOption';

interface FlexFormFields {
  readonly fixuserid: string;
  readonly iceuser: string;
  readonly ibuser: string;
}

type UsersFormValue = Omit<ApplicationUser, 'Flex'> & FlexFormFields;

interface DispatchProps {
  reloadUser(email: string): any;
}

interface OwnProps {
  readonly email: string;
  readonly readOnly?: boolean;

  onSave(user: ApplicationUser): void;

  onModifiedChanged?(modified: boolean): void;
  onErrorsChanged?(hasErrors: boolean): void;
}

type Props = OwnProps & UsersFormState & DispatchProps;
type DataProvider = Pick<UsersFormState, 'applications' | 'companies' | 'firms'>;

const toSingleFlexOktaGroupSelection = (
  applications: readonly PrismApplication[],
  previousGroups: readonly DropdownOption[],
  nextGroups: readonly DropdownOption[]
): readonly DropdownOption[] => {
  const flexApplication = applications.find((application: PrismApplication): boolean =>
    application.app_name.toLowerCase().includes('flex')
  );

  if (flexApplication === undefined) {
    return nextGroups;
  }

  const flexOktaIds = new Set(flexApplication.okta.map((group): string => String(group.id)));

  const flexSelected = nextGroups.filter((group: DropdownOption): boolean =>
    flexOktaIds.has(String(group.id))
  );

  if (flexSelected.length <= 1) {
    return nextGroups;
  }

  const previousFlexSelected = previousGroups.filter((group: DropdownOption): boolean =>
    flexOktaIds.has(String(group.id))
  );

  const newlyAdded = flexSelected.find(
    (group: DropdownOption): boolean =>
      !previousFlexSelected.some((previous: DropdownOption): boolean => previous.id === group.id)
  );

  const keptFlexGroup = newlyAdded ?? flexSelected[flexSelected.length - 1];
  const nonFlexGroups = nextGroups.filter(
    (group: DropdownOption): boolean => !flexOktaIds.has(String(group.id))
  );

  return [...nonFlexGroups, keptFlexGroup];
};

/**
 * UsersForm component for managing user information
 *
 * @param props - Component props
 * @param props.email - User email address
 * @param props.readOnly - Optional: When true, all fields become read-only and the form cannot be submitted
 * @param props.onSave - Callback when form is submitted
 * @param props.onModifiedChanged - Optional: Callback when form modification state changes
 * @param props.onErrorsChanged - Optional: Callback when form error state changes
 */
const UsersForm: React.FC<Props> = (props: Props): React.ReactElement => {
  const { email, user, readOnly = false } = props;
  const { onSave, onModifiedChanged, onErrorsChanged } = props;
  const { reloadUser } = props;
  const formValue = React.useMemo((): UsersFormValue => {
    const { Flex, ...userWithoutFlex } = user;

    return {
      ...userWithoutFlex,
      applicationId: config.defaultApplicationId,
      fixuserid: Flex.fixuserid,
      iceuser: Flex.iceuser,
      ibuser: Flex.ibuser ?? '',
    };
  }, [user]);

  const provider: DataProvider = React.useMemo(
    (): DataProvider => ({
      applications: props.applications,
      companies: props.companies,
      firms: props.firms,
    }),
    [props.applications, props.companies, props.firms]
  );

  // Modify fields based on readOnly prop and email
  const modifiedFields = React.useMemo(() => {
    // If email is empty, make all fields editable (readOnly: false)
    if (!email || email.trim() === '') {
      const modifyField = (field: any): any => {
        const modifiedField = { ...field, readOnly: false };
        if (field.children) {
          modifiedField.children = field.children.map(modifyField);
        }
        return modifiedField;
      };
      return fields.map(modifyField);
    }

    if (!readOnly) return fields;

    // Otherwise, make all fields read-only
    const modifyField = (field: any): any => {
      const modifiedField = { ...field, readOnly: true };
      if (field.children) {
        modifiedField.children = field.children.map(modifyField);
      }
      return modifiedField;
    };

    return fields.map(modifyField);
  }, [readOnly, email]);

  const onChange = React.useCallback(
    (name: string, value: any, data: ApplicationUser): FormChangeResult => {
      if (name === 'whitelist') {
        return onIpAddressesAdded(value);
      }

      if (name === 'oktaGroups' && Array.isArray(value)) {
        return {
          value: toSingleFlexOktaGroupSelection(props.applications, data.oktaGroups, value),
          error: null,
        };
      }

      return { value: value, error: null };
    },
    [props.applications]
  );

  const onSaveWrapper = React.useCallback(
    (submitted: UsersFormValue): void => {
      const { fixuserid, iceuser, ibuser, ...rest } = submitted;

      onSave({
        ...rest,
        Flex: {
          fixuserid,
          iceuser,
          ibuser: ibuser || null,
        },
      });
    },
    [onSave]
  );

  React.useEffect((): VoidFunction => {
    return reloadUser(email);
  }, [email, reloadUser]);

  return (
    <Form
      fields={modifiedFields}
      loading={props.working}
      value={formValue as unknown as ApplicationUser}
      provider={provider}
      className={styles.wide}
      onChange={onChange}
      onSubmit={onSaveWrapper as unknown as (value: ApplicationUser) => void}
      onErrorsChanged={onErrorsChanged}
      onModifiedChanged={onModifiedChanged}
      readOnly={readOnly}
    />
  );
};

const mapDispatchToProps: MapDispatchToProps<DispatchProps, OwnProps> = {
  reloadUser,
};

const mapStateToProps: MapStateToProps<UsersFormState, OwnProps, ApplicationState> = (
  applicationState: ApplicationState
): UsersFormState => applicationState.usersForm;

export default connect(mapStateToProps, mapDispatchToProps)(UsersForm);
